import { getAchievement, getSpell } from '@shefira/shared';

import { element, kitIcon } from './kit.js';
import { achievementIcon } from './achievements-tab.js';
import { uiFormat } from './text.js';
import './achievement-toast.css';

/** How long one notice stays on screen before the next takes its place. */
const SHOWN_MS = 4500;

/** One notice: its icon, the kicker line, the name, and an optional line under it. */
interface Notice {
  icon: () => HTMLElement;
  kicker: string;
  name: string;
  detail?: string;
}

/**
 * The notice of an achievement just done (ADR 0046), or of a spell just
 * learnt (ADR 0048): a gold plate at the top of the screen, one at a time,
 * the title it gives when it gives one. Only what the server said: it names
 * the achievements and the spells, this draws them.
 */
export class AchievementToast {
  private readonly root = element('div', 'achievement-toast');
  private readonly queue: Notice[] = [];
  private timer: number | undefined;

  constructor() {
    this.root.hidden = true;
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
    document.body.append(this.root);
  }

  show(ids: readonly string[]): void {
    for (const id of ids) {
      const achievement = getAchievement(id);
      if (achievement === undefined) continue;
      this.queue.push({
        icon: () => kitIcon(achievementIcon(achievement)),
        kicker: 'Succès accompli',
        name: achievement.name,
        ...(achievement.title === undefined
          ? {}
          : { detail: uiFormat('Nouveau titre : {title}', { title: achievement.title }) }),
      });
    }
    if (this.timer === undefined) this.next();
  }

  /** Spells just learnt, their own icon on the same plate. */
  showSpells(ids: readonly string[]): void {
    for (const id of ids) {
      const spell = getSpell(id);
      if (spell === undefined) continue;
      this.queue.push({
        icon: () => {
          const icon = element('span', 'kit-icon');
          icon.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
          icon.setAttribute('aria-hidden', 'true');
          return icon;
        },
        kicker: 'Nouveau sort',
        name: spell.name,
        detail: 'Place-le dans ta barre depuis le grimoire',
      });
    }
    if (this.timer === undefined) this.next();
  }

  private next(): void {
    const notice = this.queue.shift();
    if (notice === undefined) {
      this.root.hidden = true;
      this.timer = undefined;
      return;
    }
    const text = element('div', 'achievement-toast__text');
    text.append(
      element('span', 'achievement-toast__kicker', notice.kicker),
      element('span', 'achievement-toast__name', notice.name),
    );
    if (notice.detail !== undefined) {
      text.append(element('span', 'achievement-toast__title', notice.detail));
    }
    this.root.replaceChildren(notice.icon(), text);
    this.root.hidden = false;
    // Replay the entrance for each notice, not only the first.
    this.root.classList.remove('achievement-toast--in');
    requestAnimationFrame(() => {
      this.root.classList.add('achievement-toast--in');
    });
    this.timer = window.setTimeout(() => {
      this.next();
    }, SHOWN_MS);
  }

  destroy(): void {
    window.clearTimeout(this.timer);
    this.root.remove();
  }
}
